(function installFishZoneEvidenceContract(root) {
  const RESPONSE_FIELDS = Object.freeze([
    "revision",
    "ranking_ring_layer_revision_id",
    "item_id",
    "count",
    "zones",
  ]);
  const ZONE_FIELDS = Object.freeze([
    "zone_rgb",
    "zone_name",
    "known_presence_sources",
    "baseline_groups",
    "maintained_groups",
    "ranking_ring_support",
  ]);
  const RANKING_RING_SUPPORT_FIELDS = Object.freeze([
    "fully_contained_count",
    "partial_overlap_count",
  ]);
  const FISHING_GROUP_KEYS = Object.freeze([
    "prize",
    "rare",
    "high_quality",
    "general",
    "trash",
    "harpoon",
  ]);
  const FISHING_GROUP_KEY_SET = new Set(FISHING_GROUP_KEYS);
  const KNOWN_PRESENCE_SOURCES = Object.freeze(["baseline", "maintained"]);
  const KNOWN_PRESENCE_SOURCE_SET = new Set(KNOWN_PRESENCE_SOURCES);
  const RANKING_RING_ICON_NAMES = Object.freeze({
    fully_contained: "ring-full",
    partial_overlap: "ring-partial",
  });
  const U32_MAX = 0xffff_ffff;

  function exactObject(value, fields, label) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      throw new TypeError(`${label} must be an object`);
    }
    const actual = Object.keys(value).sort();
    const expected = [...fields].sort();
    if (actual.length !== expected.length || actual.some((field, index) => field !== expected[index])) {
      throw new Error(`${label} fields must be exactly: ${fields.join(", ")}`);
    }
    return value;
  }

  function requiredString(value, label) {
    if (typeof value !== "string" || !value.trim()) {
      throw new TypeError(`${label} must be a non-empty string`);
    }
    return value.trim();
  }

  function nonnegativeU32(value, label) {
    if (!Number.isSafeInteger(value) || value < 0 || value > U32_MAX) {
      throw new TypeError(`${label} must be an unsigned 32-bit integer`);
    }
    return value;
  }

  function fishingGroupKey(value, label) {
    if (typeof value !== "string" || !FISHING_GROUP_KEY_SET.has(value)) {
      throw new Error(`${label} must be one of: ${FISHING_GROUP_KEYS.join(", ")}`);
    }
    return value;
  }

  function fishingGroupArray(value, label) {
    if (!Array.isArray(value)) {
      throw new TypeError(`${label} must be an array`);
    }
    const normalized = value.map((entry, index) => (
      fishingGroupKey(entry, `${label}[${index}]`)
    ));
    if (new Set(normalized).size !== normalized.length) {
      throw new Error(`${label} must not contain duplicates`);
    }
    if (normalized.some((entry, index) => (
      index > 0
      && FISHING_GROUP_KEYS.indexOf(normalized[index - 1]) >= FISHING_GROUP_KEYS.indexOf(entry)
    ))) {
      throw new Error(`${label} must be canonically ordered`);
    }
    return Object.freeze(normalized);
  }

  function knownPresenceSourceArray(value, label) {
    if (!Array.isArray(value)) {
      throw new TypeError(`${label} must be an array`);
    }
    const normalized = value.map((entry, index) => {
      if (typeof entry !== "string" || !KNOWN_PRESENCE_SOURCE_SET.has(entry)) {
        throw new Error(
          `${label}[${index}] must be one of: ${KNOWN_PRESENCE_SOURCES.join(", ")}`,
        );
      }
      return entry;
    });
    if (new Set(normalized).size !== normalized.length) {
      throw new Error(`${label} must not contain duplicates`);
    }
    if (normalized.some((entry, index) => (
      index > 0
      && KNOWN_PRESENCE_SOURCES.indexOf(normalized[index - 1])
        >= KNOWN_PRESENCE_SOURCES.indexOf(entry)
    ))) {
      throw new Error(`${label} must be canonically ordered`);
    }
    return Object.freeze(normalized);
  }

  function fishingGroupMessageKey(value, label = "zone-evidence fishing group") {
    const group = fishingGroupKey(value, label);
    return group === "harpoon" ? "domain.method.harpoon" : `domain.loot_group.${group}`;
  }

  function fishingGroupBadgeTone(value, label = "zone-evidence fishing group") {
    const group = fishingGroupKey(value, label);
    switch (group) {
      case "prize":
        return "badge-error";
      case "rare":
        return "badge-warning";
      case "high_quality":
        return "badge-info";
      case "general":
        return "badge-success";
      default:
        return "badge-neutral";
    }
  }

  function rankingRingIconName(scope) {
    if (!Object.hasOwn(RANKING_RING_ICON_NAMES, scope)) {
      throw new Error(`unsupported zone-evidence ranking-ring scope: ${String(scope)}`);
    }
    return RANKING_RING_ICON_NAMES[scope];
  }

  function rgbKey(value, label) {
    const normalized = requiredString(value, label);
    const components = normalized.split(",");
    if (
      components.length !== 3
      || components.some((component) => !/^\d{1,3}$/.test(component))
      || components.some((component) => Number(component) > 255)
    ) {
      throw new Error(`${label} must be an r,g,b key with components from 0 to 255`);
    }
    return normalized;
  }

  function normalizeRankingRingSupport(value, label) {
    if (value === null) {
      return null;
    }
    const support = exactObject(value, RANKING_RING_SUPPORT_FIELDS, label);
    const fullyContainedCount = nonnegativeU32(
      support.fully_contained_count,
      `${label}.fully_contained_count`,
    );
    const partialOverlapCount = nonnegativeU32(
      support.partial_overlap_count,
      `${label}.partial_overlap_count`,
    );
    if (fullyContainedCount === 0 && partialOverlapCount === 0) {
      throw new Error(`${label} must contain a positive count`);
    }
    return Object.freeze({
      fully_contained_count: fullyContainedCount,
      partial_overlap_count: partialOverlapCount,
    });
  }

  function normalizeZone(value, label = "zone-evidence entry") {
    const zone = exactObject(value, ZONE_FIELDS, label);
    const normalized = {
      zone_rgb: rgbKey(zone.zone_rgb, `${label}.zone_rgb`),
      zone_name: zone.zone_name === null
        ? null
        : requiredString(zone.zone_name, `${label}.zone_name`),
      known_presence_sources: knownPresenceSourceArray(
        zone.known_presence_sources,
        `${label}.known_presence_sources`,
      ),
      baseline_groups: fishingGroupArray(zone.baseline_groups, `${label}.baseline_groups`),
      maintained_groups: fishingGroupArray(zone.maintained_groups, `${label}.maintained_groups`),
      ranking_ring_support: normalizeRankingRingSupport(
        zone.ranking_ring_support,
        `${label}.ranking_ring_support`,
      ),
    };
    if (normalized.known_presence_sources.length === 0 && normalized.ranking_ring_support === null) {
      throw new Error(`${label} must contain known presence or ranking-ring support`);
    }
    if (
      normalized.baseline_groups.length > 0
      && !normalized.known_presence_sources.includes("baseline")
    ) {
      throw new Error(`${label}.baseline_groups require baseline known presence`);
    }
    if (
      normalized.maintained_groups.length > 0
      && !normalized.known_presence_sources.includes("maintained")
    ) {
      throw new Error(`${label}.maintained_groups require maintained known presence`);
    }
    return Object.freeze(normalized);
  }

  function zoneEvidenceKind(zone, label = "zone-evidence entry") {
    if (!zone || typeof zone !== "object" || Array.isArray(zone)) {
      throw new TypeError(`${label} must be an object`);
    }
    if (zone.known_presence_sources?.length > 0) {
      return "known";
    }
    if (zone.ranking_ring_support !== null && zone.ranking_ring_support !== undefined) {
      return "potential";
    }
    throw new Error(`${label} must contain known presence or ranking-ring support`);
  }

  function zoneEvidenceMapOperator(zone, label = "zone-evidence entry") {
    const kind = zoneEvidenceKind(zone, label);
    if (
      kind === "known"
      || (zone.ranking_ring_support?.fully_contained_count ?? 0) > 0
    ) {
      return "AND";
    }
    return "OR";
  }

  function partitionZoneEvidence(zones, label = "zone-evidence entries") {
    if (!Array.isArray(zones)) {
      throw new TypeError(`${label} must be an array`);
    }
    const known = [];
    const potential = [];
    zones.forEach((zone, index) => {
      const kind = zoneEvidenceKind(zone, `${label}[${index}]`);
      (kind === "known" ? known : potential).push(zone);
    });
    return Object.freeze({
      known: Object.freeze(known),
      potential: Object.freeze(potential),
    });
  }

  function normalizeResponse(value, expectedItemId, label = "zone-evidence response") {
    const response = exactObject(value, RESPONSE_FIELDS, label);
    const revision = requiredString(response.revision, `${label}.revision`);
    if (!/^dolt:[0-9a-v]{32}$/.test(revision)) {
      throw new Error(`${label}.revision must identify an immutable Dolt commit`);
    }
    const rankingRingLayerRevisionId = requiredString(
      response.ranking_ring_layer_revision_id,
      `${label}.ranking_ring_layer_revision_id`,
    );
    if (!Number.isSafeInteger(response.item_id) || response.item_id <= 0) {
      throw new TypeError(`${label}.item_id must be a positive integer`);
    }
    if (response.item_id !== expectedItemId) {
      throw new Error(`${label}.item_id does not match the requested fish`);
    }
    if (!Array.isArray(response.zones)) {
      throw new TypeError(`${label}.zones must be an array`);
    }
    if (!Number.isSafeInteger(response.count) || response.count !== response.zones.length) {
      throw new Error(`${label}.count must match the number of zones`);
    }
    return Object.freeze({
      revision,
      ranking_ring_layer_revision_id: rankingRingLayerRevisionId,
      item_id: response.item_id,
      count: response.count,
      zones: Object.freeze(response.zones.map((zone, index) => (
        normalizeZone(zone, `${label}.zones[${index}]`)
      ))),
    });
  }

  root.__fishystuffZoneEvidenceContract = Object.freeze({
    fishingGroupArray,
    fishingGroupBadgeTone,
    fishingGroupMessageKey,
    knownPresenceSourceArray,
    normalizeResponse,
    normalizeZone,
    partitionZoneEvidence,
    rankingRingIconName,
    zoneEvidenceKind,
    zoneEvidenceMapOperator,
  });
})(typeof window === "object" ? window : globalThis);
